import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, PhoneIncoming, Copy, AlertCircle, RefreshCw, Info } from '../../icons/wp-icons';
import { easybellApi } from '../../api/easybellApi';

/**
 * Easybell-Anrufbeantworter → WerkPilot
 *
 * Easybell schickt jede Anrufbeantworter-Nachricht als E-Mail mit Audio-Anhang.
 * WerkPilot stellt pro Betrieb eine eigene Empfangsadresse bereit; der Betrieb
 * trägt sie einmalig im easybell-Anrufbeantworter ein. Eingehende Nachrichten
 * werden transkribiert, zusammengefasst, dem Kunden zugeordnet und als Anfrage
 * angelegt.
 */
function EasybellSettings({ readOnly = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setStatus(await easybellApi.getStatus());
    } catch (err) {
      setError(err.response?.data?.error || 'Status konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const run = async (fn) => {
    setWorking(true);
    setError('');
    try {
      setStatus(await fn());
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setWorking(false);
    }
  };

  const copyAddress = async () => {
    if (!status?.adresse) return;
    try {
      await navigator.clipboard.writeText(status.adresse);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard nicht verfügbar */
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center justify-center min-h-[120px]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const aktiv = !!status?.aktiv;

  return (
    <div className="bg-surface rounded-card shadow-card p-6 border-t-4" style={{ borderTopColor: '#e2001a' }}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm">
            <PhoneIncoming className="w-6 h-6" style={{ color: '#e2001a' }} />
          </div>
          <div>
            <h2 className="text-xl font-semibold">easybell Anrufbeantworter</h2>
            <p className="text-sm text-text-muted">Sprachnachrichten automatisch als Anfragen mit Transkript</p>
          </div>
        </div>

        {aktiv ? (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Aktiv
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Nicht aktiv
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">x</button>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-900">
          <strong>So funktioniert es:</strong> easybell sendet jede Anrufbeantworter-Nachricht als E-Mail mit Audiodatei.
          WerkPilot nimmt diese Mails über eine eigene Empfangsadresse entgegen und
        </p>
        <ul className="mt-2 text-sm text-blue-800 space-y-1 ml-4 list-disc">
          <li>wandelt die Sprachnachricht in Text um (Transkript),</li>
          <li>erstellt eine kurze Zusammenfassung mit Rückrufnummer und Anliegen,</li>
          <li>ordnet die Nachricht über die Telefonnummer dem bestehenden Kunden zu (sonst wird ein Neukunde angelegt, wenn ein Name genannt wurde),</li>
          <li>legt eine Anfrage an und benachrichtigt Sie.</li>
        </ul>
      </div>

      {!status?.postfachKonfiguriert && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          Das zentrale Empfangspostfach ist auf dem Server noch nicht eingerichtet. Bitte wenden Sie sich an den WerkPilot-Support.
        </div>
      )}

      {aktiv ? (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Ihre Empfangsadresse für easybell</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                readOnly
                value={status.adresse || ''}
                onFocus={(e) => e.target.select()}
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-white font-mono text-sm"
              />
              <button
                onClick={copyAddress}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-100 transition text-sm"
              >
                <Copy className="w-4 h-4" />
                {copied ? 'Kopiert!' : 'Kopieren'}
              </button>
            </div>
            <div className="mt-3 text-sm text-text-muted space-y-1">
              <p className="font-medium text-gray-700 flex items-center gap-1"><Info className="w-4 h-4" /> Einrichtung in easybell (einmalig):</p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Im easybell-Kundenportal die Cloud-Telefonanlage öffnen und den <strong>Call Flow Creator</strong> starten.</li>
                <li>Im Modul <strong>Anrufbeantworter</strong> unter „E-Mail-Adresse" die obige Adresse eintragen und speichern.</li>
                <li>Alternativ bei einem Telefonanschluss ohne Cloud-Telefonanlage: in den Einstellungen des Netz-Anrufbeantworters die Benachrichtigungs-E-Mail auf diese Adresse setzen.</li>
              </ol>
              <p className="mt-2">
                Tipp: Wenn Sie die Nachrichten weiterhin selbst per Mail erhalten möchten, richten Sie in Ihrem eigenen Postfach eine Weiterleitung an diese Adresse ein.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-white border border-gray-200 rounded-lg">
              <span className="text-xs text-text-muted">Verarbeitete Nachrichten</span>
              <div className="font-semibold">{status.anzahl || 0}</div>
            </div>
            <div className="p-3 bg-white border border-gray-200 rounded-lg">
              <span className="text-xs text-text-muted">Letzte Nachricht</span>
              <div className="font-semibold">
                {status.letzterEingang ? new Date(status.letzterEingang).toLocaleString('de-DE') : 'noch keine'}
              </div>
            </div>
          </div>

          {status.letzterFehler && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Letzter Hinweis: {status.letzterFehler}</span>
            </div>
          )}

          {!readOnly && (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => run(easybellApi.deaktivieren)}
                disabled={working}
                className="px-4 py-2 bg-danger text-white rounded-button hover:bg-red-700 transition font-medium text-sm disabled:opacity-50"
              >
                Anbindung deaktivieren
              </button>
              <button
                onClick={() => {
                  if (confirm('Neue Empfangsadresse erzeugen? Die bisherige Adresse funktioniert danach nicht mehr und muss in easybell ersetzt werden.')) {
                    run(easybellApi.tokenErneuern);
                  }
                }}
                disabled={working}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-button hover:bg-gray-50 transition text-sm disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4" />
                Neue Adresse erzeugen
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-text font-medium">easybell-Anrufbeantworter verbinden</p>
            <p className="text-sm text-text-muted mt-1">
              {readOnly
                ? 'Sie haben keine Berechtigung, diese Einstellung zu ändern.'
                : 'Nach der Aktivierung erhalten Sie eine Empfangsadresse, die Sie in easybell als Ziel für Anrufbeantworter-Nachrichten eintragen.'}
            </p>
          </div>
          {!readOnly && (
            <button
              onClick={() => run(easybellApi.aktivieren)}
              disabled={working || !status?.postfachKonfiguriert}
              className="w-full px-4 py-3 text-white rounded-button hover:opacity-90 transition font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#e2001a' }}
            >
              {working ? 'Aktiviere...' : 'Anbindung aktivieren'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default EasybellSettings;
